import React from 'react';

import { NetworkTier } from 'Containers/ResultPage/CompareModal';
import { usePremiumContext } from 'Contexts/premiumContext';
import { Numbers, NumberWithPayPeriod, Paragraph } from 'DesignLibrary/atoms';
import { PremiumPerDeduction } from 'DesignLibrary/atoms/PremiumPerDeduction';
import Plan from 'Models/plan';
import RichText from 'Shared/RichText';
import { Recommendation } from 'Types/entities';
import {
  OopEstimateTooltipWithTooltip,
  PremiumTooltipWithText,
  YearlyTotalTooltipWithText,
} from 'Utils/createTooltips';
import { toDollars } from 'Utils/helpers';

import { PricingOverviewItem, PricingOverviewWrapper } from './styled';

interface PricingOverviewProps {
  recommendation: Recommendation;
  hsaBalance?: number;
  selectedTier: NetworkTier;
}

export const PricingOverview: React.FC<PricingOverviewProps> = ({
  recommendation,
  hsaBalance,
  selectedTier,
}) => {
  const { payPeriod } = usePremiumContext();

  const plan = new Plan(recommendation);

  const outOfPocket = plan.calcOutOfPocket();

  const estimatedYearlyTotal = plan.realCostBeforeTaxBenefit;

  const defaultTier: NetworkTier = plan.hasMostPreferredNetwork ? 'most_preferred' : 'in_network';
  const showEstimates = selectedTier === defaultTier;

  const premiumComponent = (
    <PremiumPerDeduction
      annualPremium={plan.annualPremium}
      deductions={plan.annualPremiumDeductions}
      currency
      size="large"
      keyHint={`plan-${plan.planId}-premium-value`}
    />
  );

  return (
    <PricingOverviewWrapper>
      <PricingOverviewItem>
        <YearlyTotalTooltipWithText payPeriod={payPeriod} as="h3" />
        {showEstimates ? (
          <NumberWithPayPeriod
            payPeriodType="yearly"
            size="large"
            fontWeight="bold"
            value={estimatedYearlyTotal}
            keyHint={`plan-${plan.planId}-estimated-yearly-total`}
          />
        ) : (
          <Numbers size="large">N/A</Numbers>
        )}
      </PricingOverviewItem>

      <PricingOverviewItem>
        <PremiumTooltipWithText payPeriod={payPeriod} as="h3" keyHint={`plan-${plan.planId}-premium`} />
        {premiumComponent}
      </PricingOverviewItem>

      <PricingOverviewItem>
        {!showEstimates ? (
          <>
            <OopEstimateTooltipWithTooltip as="h3" keyHint={`plan-${plan.planId}-oop`} />
            <Numbers size="large">N/A</Numbers>
          </>
        ) : hsaBalance !== undefined && hsaBalance > 0 && outOfPocket > 0 ? (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <NumberWithPayPeriod
                value={outOfPocket}
                payPeriodType="yearly"
                size="large"
                keyHint={`plan-${plan.planId}-oop-value`}
              />
              <OopEstimateTooltipWithTooltip as="h3" keyHint={`plan-${plan.planId}-oop`} />
            </div>
            <Paragraph size="mini" color="--text-gray" data-testid="hsa-balance" style={{ order: 3 }}>
              <RichText
                field="health_section.recommendation.hsa_balance"
                vars={[toDollars(hsaBalance)]}
                inlineParagraphs={false}
              />
            </Paragraph>
          </>
        ) : (
          <>
            <OopEstimateTooltipWithTooltip as="h3" keyHint={`plan-${plan.planId}-oop`} />
            <NumberWithPayPeriod
              value={outOfPocket}
              payPeriodType="yearly"
              size="large"
              keyHint={`plan-${plan.planId}-oop-value`}
            />
          </>
        )}
      </PricingOverviewItem>
    </PricingOverviewWrapper>
  );
};
